import React, { useReducer } from 'react';
import { userAppData } from '../MainApp.js';
import { StandardSingleLineTextEntry } from '../common/formComponents/StandardSingleLineTextEntry.js';
import { StandardMultiLineTextEntry } from '../common/formComponents/StandardMultiLineTextEntry.js';
import { StandardDateSelector } from '../common/formComponents/StandardDateSelector.js';
import {
  FileDropzone,
  SubmitButton,
} from '../common/formComponents.js';
import { RequestSubmittedModal } from '../common/modals.js';
import { SEC_ApiCall, SEC_FileApiPost, SEC_DataApiPost, fileUploadFormDataGenerator } from '../func.js';

// Initial States
const initialFormState = {
  title: '',
  location: '',
  description: '',
  requiredDate: '0000-00-00',
  files: [],
  errors: {},
  modalState: { active: false, apiState: 'loading', successId: null },
};

// Reducer Function
function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_ERROR':
      return { ...state, errors: { ...state.errors, [action.field]: action.error } };
    case 'CLEAR_ERRORS':
      return { ...state, errors: {} };
    case 'SET_MODAL':
      return { ...state, modalState: { ...state.modalState, ...action.payload } };
    case 'RESET_FORM':
      return initialFormState;
    default:
      return state;
  }
}

export default function ItSupportForm() {
  const [state, dispatch] = useReducer(formReducer, initialFormState);

  // Utility Functions
  const validateField = (field, value) => {
    if (!value || (field === 'requiredDate' && value === '0000-00-00')) {
      return `${field} cannot be blank`;
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    ['title', 'location', 'description', 'requiredDate'].forEach((field) => {
      const error = validateField(field, state[field]);
      if (error) errors[field] = error;
    });
    return errors;
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch({ type: 'CLEAR_ERRORS' });
    dispatch({ type: 'SET_MODAL', payload: { active: true, apiState: 'loading' } });

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      dispatch({ type: 'SET_ERROR', field: 'form', error: errors });
      dispatch({ type: 'SET_MODAL', payload: { active: false } });
      return;
    }

    // Prepare data
    const timestamp = new Date().toISOString();
    const eventObject = {
      primaryEvent: { user: userAppData['cogId'], timestamp, eventData: state.description },
      additionalEvents: [],
    };

    const formObj = {
      title: state.title,
      location: state.location,
      events: eventObject,
      requiredDate: state.requiredDate,
      fileNames: null,
      subDate: timestamp.split('T')[0],
    };

    // Handle file uploads if files exist
    if (state.files.length > 0) {
      try {
        const fileUploadPromises = state.files.map(async (file) => {
          const uploadUrl = await SEC_ApiCall('general/SignedURLs?type=post&path=itRequests');
          const formData = fileUploadFormDataGenerator(uploadUrl, file);
          await SEC_FileApiPost(uploadUrl.url, formData);
          return uploadUrl.fields.key;
        });
        formObj.fileNames = await Promise.all(fileUploadPromises);
      } catch (err) {
        console.error('File upload failed:', err);
        dispatch({ type: 'SET_MODAL', payload: { active: true, apiState: 'error' } });
        return;
      }
    }

    // Submit data
    try {
      const response = await SEC_DataApiPost('it-requests', JSON.stringify(formObj), {},
        (error) => dispatch({ type: 'SET_MODAL', payload: { active: true, apiState: 'error' } })
      );

      const rid = response?.requestId; // Safely access response.requestId

      if (!rid || rid === 'error') {
        throw new Error('Invalid or missing ID in response');
      }

      dispatch({
        type: 'SET_MODAL',
        payload: { active: true, apiState: 'success', successId: rid },
      });
    } catch (err) {
      console.error('Submission error:', err);
      dispatch({ type: 'SET_MODAL', payload: { active: true, apiState: 'error' } });
    }

  };

  const closeModal = () => {
    if (state.modalState.apiState === 'success') {
      dispatch({ type: 'RESET_FORM' }); // Reset the form to initial state
    }
    dispatch({ type: 'SET_MODAL', payload: { active: false } }); // Close the modal
  };


  return (
    <div className='internal_main_div'>
      <div className='header_div'>
        <h1 className='admin_header'>IT Support Form</h1>
      </div>
      <div className='formDiv'>
        <form onSubmit={handleSubmit}>
          <RequestSubmittedModal
            modalActiveState={state.modalState.active}
            requestType='IT'
            rid={state.modalState.successId}
            apiState={state.modalState.apiState}
            onModalClose={closeModal}
          />

          <StandardSingleLineTextEntry
            title='Request Title'
            data={state.title}
            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'title', value })}
            errorText={state.errors.title}
          />
          <StandardSingleLineTextEntry
            title='Location'
            data={state.location}
            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'location', value })}
            errorText={state.errors.location}
          />
          <StandardMultiLineTextEntry
            title='Description'
            data={state.description}
            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'description', value })}
            errorText={state.errors.description}
          />
          <StandardDateSelector
            title="Required Date"
            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'requiredDate', value })}
          />

          <FileDropzone
            filesState={state.files}
            fileStateSet={(files) => dispatch({ type: 'SET_FIELD', field: 'files', value: files })}
          />
          <SubmitButton text='Submit Request' />
        </form>
      </div>
    </div>
  );
}
